import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MapPin, ShoppingBag, Calendar, Users, Crown, User, LogOut, Menu, X, BookOpen, MessageCircle, Trophy, Fish, Award, Search } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import NotificationBell from './NotificationBell';
import BrandLogo from './BrandLogo';
import Footer from './Footer';
import { trackTierGate } from '../lib/tierGateTracker';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = () => {
  const { user, logout, isAuthenticated, loading, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  // Handle authentication redirect.
  // Public sub-routes (member discovery + public profiles) still render the
  // shell so navigation/footer work; they just don't kick anonymous viewers
  // to /login. Backend endpoints behind them already accept anon callers
  // via get_optional_user.
  const PUBLIC_PATHS = ['/members', '/learn'];
  const PUBLIC_PREFIXES = ['/u/'];
  const isPublicShellRoute =
    PUBLIC_PATHS.includes(location.pathname) ||
    PUBLIC_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (!loading && !isAuthenticated && !isPublicShellRoute) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate, isPublicShellRoute]);

  // Sticky-shrink on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fetch unread message count
  useEffect(() => {
    if (token) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30 seconds
      return () => clearInterval(interval);
    }
  }, [token]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Fish },
    { path: '/lakes', label: 'Lakes', icon: MapPin },
    { path: '/shop', label: 'Shop', icon: ShoppingBag },
    { path: '/learn', label: 'Learn', icon: BookOpen },
    { path: '/league', label: 'League', icon: Trophy },
    { path: '/competitions', label: 'Compete', icon: Award },
    { path: '/events', label: 'Events', icon: Calendar },
    { path: '/community', label: 'Community', icon: Users },
  ];

  const isActive = (path) => location.pathname === path;

  const getTierColor = (tier) => {
    switch (tier) {
      case 'elite': return 'text-[#c8b273]';
      case 'pro': return 'text-[#ff5e00]';
      default: return 'text-[#6b7d6b]';
    }
  };

  // Show loading state while checking authentication. For PUBLIC shell
  // routes (member discovery / public profiles) we render the layout even
  // when no user is logged in.
  if (loading || (!isAuthenticated && !isPublicShellRoute)) {
    return (
      <div className="min-h-screen bg-[#050605] flex items-center justify-center">
        <div className="text-center" data-testid="loading-splash">
          <BrandLogo variant="splash" as="div" testId="loading-splash-logo" />
          <p className="text-[#8a948a] mt-6 text-sm uppercase tracking-[0.3em]">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050605]">
      {/* Suspension banner — visible across the app if the admin has suspended this account. */}
      {user?.is_suspended && (
        <div
          className="bg-red-600 text-white text-sm px-4 py-2.5 text-center sticky top-0 z-[60] border-b border-red-800"
          data-testid="suspension-banner"
        >
          <strong className="font-bold">Account suspended.</strong>{' '}
          {user.suspension_note
            ? <span>Reason: {user.suspension_note}.</span>
            : <span>Posting and submissions are disabled.</span>}{' '}
          <a href="mailto:support@thecarpcast.co.uk" className="underline">Contact support</a>
        </div>
      )}
      {/* Header — fixed, glass, sticky-shrink on scroll */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b transition-all duration-300 ${
          scrolled
            ? 'bg-[#050605]/85 border-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.5)]'
            : 'bg-[#050605]/60 border-white/5'
        }`}
        data-testid="app-header"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div
            className={`grid grid-cols-[auto_1fr_auto] items-center gap-4 lg:gap-8 transition-[height] duration-300 ${
              scrolled ? 'h-16' : 'h-20'
            }`}
          >
            {/* Logo (left) — full on sm+, icon-only on small mobile */}
            <div className="flex items-center shrink-0 min-w-0">
              <BrandLogo
                variant="full"
                size={scrolled ? 'md' : 'lg'}
                className="hidden sm:inline-flex"
                testId="navbar-logo-full"
              />
              <BrandLogo
                variant="icon"
                size="md"
                className="sm:hidden"
                testId="navbar-logo-icon"
              />
            </div>

            {/* Desktop Nav (centered) — icons on lg, icon + label on xl */}
            <nav className="hidden lg:flex items-center justify-center gap-0.5 xl:gap-1" data-testid="desktop-nav">
              {navItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    title={item.label}
                    className={`group relative flex items-center gap-1.5 px-2.5 xl:px-3.5 py-2 rounded transition-all duration-200 ${
                      active
                        ? 'text-[#c8b273] bg-[#c8b273]/8'
                        : 'text-[#8a948a] hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="text-xs xl:text-sm uppercase tracking-wide hidden xl:inline">{item.label}</span>
                    {active && (
                      <span className="absolute -bottom-px left-2 right-2 h-px bg-[#c8b273]" />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Spacer for medium screens where nav is hidden */}
            <div className="hidden md:block lg:hidden" />

            {/* User controls (right) */}
            <div className="flex items-center gap-2 md:gap-3 justify-end" data-testid="navbar-controls">
              <Link
                to="/members"
                className="relative p-2 text-[#8a948a] hover:text-white transition-colors"
                data-testid="members-link"
                aria-label="Discover anglers"
                title="Discover anglers"
              >
                <Search className="w-5 h-5" />
              </Link>

              {isAuthenticated && <NotificationBell token={token} />}

              {isAuthenticated && (
                <Link
                  to="/messages"
                  className="relative p-2 text-[#8a948a] hover:text-white transition-colors"
                  data-testid="messages-link"
                  aria-label="Messages"
                >
                  <MessageCircle className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#ff5e00] rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>
              )}

              {isAuthenticated ? (
                <Link
                  to="/subscription"
                  data-testid="subscription-link"
                  onClick={() => trackTierGate({
                    surface: 'subscription_button',
                    feature: 'navbar.tier_pill',
                    requiredTier: user?.subscription_tier === 'basic' ? 'pro' : 'elite',
                    metadata: { current_tier: user?.subscription_tier },
                  })}
                  className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded text-xs uppercase tracking-wider font-bold border ${getTierColor(user?.subscription_tier)} border-current/40 hover:bg-white/5 transition-colors`}
                >
                  <Crown className="w-3.5 h-3.5" />
                  {user?.subscription_tier}
                </Link>
              ) : (
                <Link
                  to="/login"
                  data-testid="navbar-login-link"
                  className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded text-xs uppercase tracking-wider font-bold border border-[#c8b273]/40 text-[#c8b273] hover:bg-[#c8b273]/10 transition-colors"
                >
                  Sign in
                </Link>
              )}

              {isAuthenticated && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="hidden md:flex items-center gap-2 hover:bg-white/5" data-testid="user-menu-trigger">
                      <div className="w-8 h-8 rounded-full bg-[#141714] border border-[#c8b273]/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-[#c8b273]" />
                      </div>
                      <span className="text-sm text-white truncate max-w-[120px]">{user?.username}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-[#0b0d0b] border-white/10">
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2" data-testid="profile-link">
                        <User className="w-4 h-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/subscription" className="flex items-center gap-2">
                        <Crown className="w-4 h-4" />
                        Subscription
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-red-400" data-testid="logout-btn">
                      <LogOut className="w-4 h-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-white hover:bg-white/5 rounded transition-colors"
                data-testid="mobile-menu-btn"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <nav className="lg:hidden border-t border-white/5 bg-[#0b0d0b]" data-testid="mobile-nav">
            {isAuthenticated ? (
              <div className="flex md:hidden items-center justify-between px-6 py-3 border-b border-white/5">
                <Link
                  to="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 text-white"
                >
                  <div className="w-9 h-9 rounded-full bg-[#141714] border border-[#c8b273]/30 flex items-center justify-center">
                    <User className="w-4 h-4 text-[#c8b273]" />
                  </div>
                  <span className="text-sm">{user?.username}</span>
                </Link>
                <Link
                  to="/subscription"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wider border border-current/40 ${getTierColor(user?.subscription_tier)}`}
                >
                  <Crown className="w-3.5 h-3.5" />
                  {user?.subscription_tier}
                </Link>
              </div>
            ) : (
              <div className="flex md:hidden items-center justify-end px-6 py-3 border-b border-white/5">
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider border border-[#c8b273]/40 text-[#c8b273]"
                >
                  Sign in
                </Link>
              </div>
            )}
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-6 py-4 border-b border-white/5 transition-colors ${
                  isActive(item.path) ? 'text-[#c8b273] bg-white/5' : 'text-[#8a948a]'
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="uppercase tracking-wide">{item.label}</span>
              </Link>
            ))}
            {isAuthenticated && (
              <button
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                className="flex items-center gap-3 px-6 py-4 text-red-400 w-full"
              >
                <LogOut className="w-5 h-5" />
                <span className="uppercase tracking-wide">Logout</span>
              </button>
            )}
          </nav>
        )}
      </header>

      {/* Spacer matching header height (h-20 default / h-16 scrolled) */}
      <main className={`min-h-screen transition-[padding] duration-300 ${scrolled ? 'pt-16' : 'pt-20'}`}>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
